import { z } from 'zod';
import { centAmount, isoDate, positiveAmount, uuid } from './posting.js';
/**
 * Die Verträge der Guthaben-Einlösung — Auftrag `C-gueinloesung` (Plan
 * `docs/superpowers/plans/2026-08-26-sachkonten-abgleich.md`, Task 15; Spec
 * `docs/superpowers/specs/2026-08-25-guthaben-ohne-debitoren.md`, Abschnitt
 * 3.5).
 *
 * **Nur die zweite Stufe.** Die Rechnung selbst (Stufe 1: `1400` an Erlös +
 * USt) entsteht über die bestehenden Belegendpunkte
 * (`POST /api/v1/documents/:id/issue`, `documents.ts`). Dieser Vertrag deckt
 * ausschliesslich die **Zahlung aus Guthaben** — `KONTO_GUTHABEN` an `1400` —,
 * die eine bereits ausgestellte Rechnung anschliessend ganz oder teilweise
 * ausgleicht (`GU-5`, Annahme `A-K2`).
 */
/**
 * `POST /api/v1/guthaben/einloesungen` — der Rumpf.
 *
 * **`betrag` ist Pflicht und wird nicht aus der Rechnung abgeleitet.** Eine
 * Rechnung kann teils aus Guthaben und teils auf einem anderen Weg beglichen
 * werden (Mischzahlung); ein stillschweigender Rückgriff auf `grossTotal`
 * verböte genau diesen Fall und wäre ausserdem eine zweite Instanz derselben
 * Zahl, deren Herleitung woanders steht.
 *
 * **`idempotencyKey` ist optional und trägt hier mehr als anderswo** — Auftrag
 * `V-idempotenzschluessel`, Befund `U-2` des Routen-Inventars. Genau der Satz
 * darüber, der die Mischzahlung erlaubt, nimmt dieser Route die Möglichkeit,
 * eine Wiederholung an den Daten selbst zu erkennen: Zwei Anfragen über je
 * 50,00 € gegen dieselbe Rechnung über 119,00 € sind fachlich **entweder**
 * zwei Teilzahlungen **oder** ein Netzwerk-Wiederholung derselben Zahlung, und
 * der Rumpf allein sagt nicht, welches von beidem. `GUTHABEN_BETRAG_UEBERSTEIGT_RECHNUNG`
 * (`E-410`) fängt nur den Sonderfall ab, bei dem die erste Einlösung die
 * Rechnung bereits voll ausgeglichen hat. Der Schlüssel ist deshalb das
 * einzige Merkmal, mit dem der Aufrufer „dies ist dieselbe Zahlung" sagen
 * kann; ohne ihn bleibt jede Teil-Einlösung eine neue Zahlung, und das ist
 * richtig so.
 *
 * Form und Wirkung wie bei `issueInputSchema`/`cancelInputSchema`
 * (`documents.ts`): nicht leer, und der Wert läuft bis
 * `journal_entry.idempotency_key` durch (Unique-Index
 * `journal_entry_idempotency` auf `(source_system, idempotency_key)`, hier mit
 * `source_system = 'guthaben.einloesung'`). Eine Wiederholung mit demselben
 * Schlüssel und demselben Inhalt liefert die ERSTE Antwort zurück; derselbe
 * Schlüssel mit abweichendem Inhalt ist `IDEMPOTENCY_CONFLICT`.
 */
export const guthabenEinloesungRequestSchema = z
    .object({
    documentId: uuid,
    betrag: positiveAmount,
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
})
    .strict();
/**
 * Die Antwort — eine geschriebene Buchung, keine neue Ablage.
 *
 * Es gibt keine eigene `guthaben_einloesung`-Tabelle: Der Beleg dieser
 * Zahlung ist der Journaleintrag selbst (`journal_entry`,
 * `source_system = 'guthaben.einloesung'`, `document_id` auf die eingelöste
 * Rechnung). Eine zweite Ablage wäre die Art Dopplung, die auseinanderläuft,
 * sobald eine Stornierung nur die eine Seite träfe.
 */
export const guthabenEinloesungSchema = z
    .object({
    journalEntryId: uuid,
    entryNo: z.string(),
    documentId: uuid,
    /** Die gedruckte Rechnungsnummer, wie sie im Buchungstext steht (`FA-…`). */
    documentNumber: z.string(),
    betrag: positiveAmount,
    bookingDate: isoDate,
})
    .strict();
/**
 * `POST /api/v1/guthaben/einloesungen/:journalEntryId/storno` — der Rückweg
 * für eine irrtümliche Einlösung. Auftrag `C-einloesungsstorno` (2026-08-30),
 * Design-Scribble `docs/scribbles/2026-08-30-einloesungsstorno.md`,
 * Variante A2.
 *
 * **`begruendung` ist Pflicht** — dieselbe Form wie
 * `kontierungStornoRequestSchema` (`kontierung.ts`): Es gibt keinen Beleg,
 * der die Nachvollziehbarkeit ersetzen könnte.
 *
 * **`idempotencyKey` optional, dieselbe Form wie bei der Einlösung selbst**
 * (`guthabenEinloesungRequestSchema` oben, Auftrag `V-idempotenzschluessel`).
 * Eine Wiederholung mit demselben Schlüssel und demselben Inhalt liefert die
 * ERSTE Antwort zurück statt ein zweites Mal zu stornieren; derselbe
 * Schlüssel mit abweichendem Inhalt ist `IDEMPOTENCY_CONFLICT`.
 *
 * **`GU-15` (2026-08-30): kein Vier-Augen-Feld.** Anders als
 * `kontierungStornoRequestSchema` verlangt dieser Vertrag keine zweite Person
 * — die Rechnung dahinter ist bereits freigegeben, ein zweites Tor über ihrer
 * Zahlung wäre eine Kontrolle ohne eigenen Fachgegenstand (Begründung im
 * Kopfkommentar von `einloesung.controller.ts`).
 */
export const guthabenEinloesungStornoRequestSchema = z
    .object({
    begruendung: z.string().trim().min(1, 'Begründung darf nicht leer sein').max(2000),
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
})
    .strict();
/**
 * Die Antwort des Einlösungsstorno — die Gegenbuchung, keine neue Ablage
 * (dieselbe Begründung wie bei `guthabenEinloesungSchema` oben).
 *
 * **`stornierteEinloesungId` zusätzlich zu `journalEntryId`.** `journalEntryId`
 * ist hier die neu entstandene STORNOBUCHUNG (die Gegenbuchung); ein Aufrufer,
 * der wissen will, WELCHE Einlösung storniert wurde, braucht beide Kennungen
 * getrennt — anders als bei `guthabenEinloesungSchema`, wo es nur die eine
 * Buchung gibt.
 */
export const guthabenEinloesungStornoSchema = z
    .object({
    journalEntryId: uuid,
    entryNo: z.string(),
    stornierteEinloesungId: uuid,
    documentId: uuid,
    documentNumber: z.string(),
    betrag: positiveAmount,
    bookingDate: isoDate,
})
    .strict();
/**
 * Die Verträge der Guthaben-Erstattung — Auftraggeberwunsch vom 2026-08-28
 * (Kunde löscht sein Konto, Restguthaben wird ausgezahlt), Auftrag
 * `V-guthabenauszahlung`.
 *
 * **Rechtslage.** Die Erstattung eines Mehrzweck-Gutschein-Guthabens ist KEIN
 * Umsatz — § 3 Abs. 15 S. 2 UStG, UStAE 3.17 („Rücktausch von
 * Zahlungsmitteln") — Selbstklärungs-Scribble Punkt 11,
 * `docs/scribbles/2026-08-28-selbstklaerung-steuerfragen.md`. Die Buchung
 * trägt deshalb keine Steuerzeile.
 *
 * **Zweistufig, mit Vier-Augen — nach dem Kontierungs-Muster
 * (`kontierung.ts`).** Anders als die Einlösung (eine einzige, sofort
 * buchende Fähigkeit) verlässt bei einer Erstattung tatsächlich Geld das
 * Haus; die Anlage legt nur den Vorgang an, die Freigabe bucht.
 */
export const guthabenErstattungZahlwegSchema = z.enum(['ueberweisung', 'paypal', 'mollie']);
export const guthabenErstattungStatusSchema = z.enum(['angelegt', 'freigegeben']);
/**
 * `POST /api/v1/guthaben/erstattungen` — die Anlage.
 *
 * **`referenz` ist FREITEXT.** Das System löst keine echte Zahlung aus — die
 * Überweisung oder der Refund beim Zahlungsanbieter passiert ausserhalb
 * dieses Systems; dieser Datensatz bucht die Auszahlung nur nach. `referenz`
 * ist der Ort für einen IBAN-Hinweis oder eine PayPal-/Mollie-
 * Transaktionsreferenz, damit die Buchung nachvollziehbar bleibt.
 *
 * **`begruendung` ist Pflicht**, anders als bei der Einlösung: Eine
 * Erstattung ist ein Ausnahmefall (typisch: Kontolöschung), keine
 * routinemässige Zahlung gegen einen Beleg, und die Begründung trägt die
 * Nachvollziehbarkeit, die hier kein Beleg liefert.
 */
export const guthabenErstattungCreateRequestSchema = z
    .object({
    customerId: uuid,
    betrag: positiveAmount,
    zahlweg: guthabenErstattungZahlwegSchema,
    referenz: z.string().trim().min(1, 'Referenz darf nicht leer sein'),
    begruendung: z.string().trim().min(1, 'Begründung darf nicht leer sein'),
})
    .strict();
/**
 * `POST /api/v1/guthaben/erstattungen/:id/freigabe` — der Rumpf.
 *
 * **`idempotencyKey` wird hier und nicht bei der Anlage durchgereicht** (wie
 * bei der Einlösung, `V-idempotenzschluessel`/Zug 71) — die BUCHUNG entsteht
 * erst bei der Freigabe, und genau die Wiederholung DIESES Aufrufs (nach
 * einem Netzwerkabbruch) ist der Fall, den der Schlüssel abfängt.
 */
export const guthabenErstattungFreigabeRequestSchema = z
    .object({
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
})
    .strict();
/**
 * Eine Guthaben-Erstattung — die Antwort von Anlage, Freigabe und der
 * Wartend-Liste.
 *
 * **`journalEntryId` ist NUR bei `status === 'freigegeben'` gesetzt** — die
 * Anlage bucht nicht (Migration `0095`, `guthaben_erstattung_angelegt_unvollstaendig`).
 */
export const guthabenErstattungSchema = z
    .object({
    id: uuid,
    customerId: uuid,
    betrag: positiveAmount,
    zahlweg: guthabenErstattungZahlwegSchema,
    referenz: z.string(),
    begruendung: z.string(),
    status: guthabenErstattungStatusSchema,
    journalEntryId: uuid.nullable(),
    createdBy: uuid,
    createdAt: z.string(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /**
     * Einzelfreigabe (`D-31`, `0263`): Wer angelegt hat, hat auch freigegeben —
     * nur unter einer Freigaberegel ohne vier Augen. `optional()`, weil ältere
     * Fixturen das Feld nicht führen.
     */
    einzelfreigabe: z.boolean().optional(),
})
    .strict();
/**
 * `GET /api/v1/guthaben/erstattungen/wartend` — die Anlagen, die noch auf
 * ihre Freigabe warten. Dieselbe Bauform wie `KontierungWartendListe`
 * (`W-freigabeliste`).
 *
 * **Umbenannt von `guthabenErstattungWartendListSchema` zu
 * `…ListeSchema`** (`V-vertrag3`, 2026-08-29): Der OpenAPI-Erzeuger löst
 * einen Rückgabetyp über `schemaNameZuTyp()` rein am Namen auf
 * (`apps/api/src/openapi/aufloesung.ts`) — für den Typ
 * `GuthabenErstattungWartendListe` verlangt das zwingend
 * `guthabenErstattungWartendListeSchema`, sonst bleibt die Route
 * `[UNGEKLÄRT]`, egal wie korrekt das Schema sonst ist. Derselbe Befund wie
 * bei `incomingPrefillSchema` (`O-vertraege2`, Handbuch 3.359).
 */
export const guthabenErstattungWartendListeSchema = z
    .object({
    erstattungen: z.array(guthabenErstattungSchema),
})
    .strict();
/**
 * Die LESENDEN Verträge der Guthabenabstimmung — Auftrag `W-abstimmansicht`.
 *
 * **Der einzige Schreiber bleibt `pnpm guthaben:abstimmung`
 * (`apps/api/src/guthaben/abstimmung.ts`).** Diese Schemas beschreiben
 * ausschliesslich, was `GET /api/v1/guthaben/abstimmungen` und
 * `GET /api/v1/guthaben/abstimmungen/:id` aus der Tabelle
 * `guthaben_abstimmung` (Migration `0070`, Spalte `gleichung1` seit `0085`)
 * zurückgeben — kein Weg über HTTP schreibt hier einen Satz an.
 *
 * **Fünf Werte, von Hand neben `BEFUNDKLASSEN` gepflegt.** `befunde` ist in
 * der Datenbank `jsonb` ohne eigenen CHECK auf den Inhalt (nur auf die Form,
 * `guthaben_abstimmung_befunde_liste` — siehe Migration `0070`); die
 * Werteliste hier ist deshalb keine Ableitung aus einem Datenbank-Enum,
 * sondern die Abschrift von `BEFUNDKLASSEN`
 * (`apps/api/src/guthaben/abstimmung.ts`). Eine sechste Klasse dort ohne
 * Nachtrag hier lässt eine Antwort mit dieser Klasse am `.strict()`-Objekt
 * scheitern — sichtbar am Frontend, nicht still verschluckt.
 */
export const guthabenAbstimmungBefundklasseSchema = z.enum([
    'datei_unstimmig',
    'dublette',
    'negativ',
    'differenz',
    'unbebucht',
]);
/** Ein einzelner Befund — siehe `Befund` in `apps/api/src/guthaben/abstimmung.ts`. */
export const guthabenAbstimmungBefundSchema = z
    .object({
    klasse: guthabenAbstimmungBefundklasseSchema,
    text: z.string(),
    /** Nur bei `klasse === 'dublette'` belegt. */
    kundennummer: z.string().optional(),
})
    .strict();
/**
 * Die fünf Positionen der Kanzlei-Formel (Gleichung 1) — Abschrift von
 * `GLEICHUNG1_POSITIONEN` (`apps/api/src/guthaben/abstimmung.ts`), aus
 * demselben Grund wie bei `guthabenAbstimmungBefundklasseSchema` oben.
 */
export const gleichung1PositionSchema = z.enum([
    'aufladungen',
    'einloesungen',
    'erstattungen',
    'korrekturen',
    'ausbuchungen',
]);
/** Eine Zeile der Gleichung-1-Komponententabelle — siehe `Gleichung1Komponente`. */
export const gleichung1KomponenteSchema = z
    .object({
    position: gleichung1PositionSchema,
    bezeichnung: z.string(),
    /**
     * Der signierte Beitrag (Haben minus Soll) dieser Herkunft. `centAmount` und
     * nicht `positiveAmount`: Eine Korrektur kann den Saldo in beide Richtungen
     * verschieben (Storno einer Aufladung senkt ihn, Storno einer Einlösung hebt
     * ihn), siehe der Kopfkommentar von `Gleichung1Komponente`.
     */
    betrag: centAmount,
    /** Zahl der zugrundeliegenden Journalbuchungen. `null` ohne Buchungspfad. */
    anzahl: z.number().int().nonnegative().nullable(),
    /** Nur gesetzt, wenn (noch) kein Buchungspfad existiert. */
    vermerk: z.string().optional(),
})
    .strict();
/** Die erste Gleichung der Kanzlei-Formel — siehe `Gleichung1Bericht`. */
export const gleichung1BerichtSchema = z
    .object({
    /** Bauartbedingt immer `0.00` — siehe der Kopfkommentar von `Gleichung1Bericht`. */
    anfangsbestand: centAmount,
    komponenten: z.array(gleichung1KomponenteSchema),
    endbestand: centAmount,
    differenz1: centAmount,
    unbebuchterAltbestand: z
        .object({
        anzahl: z.number().int().nonnegative(),
        summe: centAmount,
    })
        .strict(),
})
    .strict();
/**
 * Ein Abgleichsatz in der Liste — `GET /api/v1/guthaben/abstimmungen`.
 *
 * **Ohne `befunde` und `gleichung1`.** Die Liste zeigt die Kennzahlen, an
 * denen ein Anwender einen Satz erkennt und auswählt; die Einzelheiten holt
 * die Detailseite über `GET .../:id` nach. Dasselbe Muster wie
 * `documentSummarySchema`/`documentViewSchema` (Liste knapp, Detail
 * vollständig).
 */
export const guthabenAbstimmungListItemSchema = z
    .object({
    id: uuid,
    stichtag: isoDate,
    zeilen: z.number().int().nonnegative(),
    summeExport: centAmount,
    saldoKonto: centAmount,
    differenz: centAmount,
    createdAt: z.string(),
})
    .strict();
/**
 * `GET /api/v1/guthaben/abstimmungen` — neueste zuerst.
 *
 * **Umbenannt von `guthabenAbstimmungListSchema` zu `…ListeSchema`**
 * (`V-vertrag3`, 2026-08-29) — derselbe Namenskonventionsbefund wie bei
 * `guthabenErstattungWartendListeSchema` oben: Der Rückgabetyp
 * `GuthabenAbstimmungListe` löst nur über `guthabenAbstimmungListeSchema` auf
 * (`schemaNameZuTyp`, `apps/api/src/openapi/aufloesung.ts`).
 */
export const guthabenAbstimmungListeSchema = z
    .object({
    saetze: z.array(guthabenAbstimmungListItemSchema),
})
    .strict();
/**
 * Ein Abgleichsatz vollständig — `GET /api/v1/guthaben/abstimmungen/:id`.
 *
 * `quelleStorageKey` steht mit dabei, aber die Datei selbst liefert dieser
 * Endpunkt nicht aus — sie liegt unverändert im Archiv (Spec 5.4/5.6); wer sie
 * braucht, liest sie dort, nicht über diese Antwort.
 */
export const guthabenAbstimmungDetailSchema = guthabenAbstimmungListItemSchema
    .extend({
    quelleSha256: z.string().regex(/^[0-9a-f]{64}$/, 'Kein gültiger SHA-256-Hash'),
    quelleStorageKey: z.string(),
    quelleByteSize: z.number().int().positive(),
    befunde: z.array(guthabenAbstimmungBefundSchema),
    gleichung1: gleichung1BerichtSchema,
    createdBy: uuid,
})
    .strict();
/**
 * Die Verträge der Guthaben-Korrektur — Owner-Entscheide `GU-9` **B** und
 * `GU-10` vom 2026-09-01, Spec
 * `docs/superpowers/specs/2026-09-02-guthaben-korrekturen-design.md`
 * Abschnitt 8.1, Auftrag `C-gukorrcrud` (Task 4 des Umsetzungsplans).
 *
 * **Sechs Endpunkte, kein `PATCH` und kein `DELETE`.** `guthaben_korrektur`
 * ist anfügend (`0126`, `E-687`): Eine falsche Anlage wird nicht geändert,
 * sondern nicht freigegeben. Ein `PATCH`-Vertrag hier wäre die Einladung,
 * genau das zu versuchen — und die Ablehnung käme dann als roher `23001` aus
 * einem Trigger statt als Aussage über die Sache.
 *
 * **Der Grund ist eine Aufzählung und kein Freitext** (`D-7`). Der Vertrag
 * ist die **erste** Stelle, an der ein unbekannter Grund scheitert; die
 * zweite ist `gegenkontoFuer()` im Dienst (`E-693`), die dritte die
 * `CHECK`-Liste `guthaben_korrektur_grund_bekannt` der Migration `0126`.
 * Alle drei führen denselben Katalog, und `E-703` hält sie gegeneinander —
 * ohne den Fall laufen sie binnen Wochen auseinander.
 *
 * **Beim Bau trägt die Aufzählung GENAU einen Wert** (`D-8`, Grenze `A-3`).
 * Das ist kein Versehen, sondern die Weigerung zu schätzen: `anfangsbestand`
 * ist der einzige Grund, dessen Gegenseite heute entschieden ist. Verfall
 * (`S-7`) und Kulanz (`GU-6`) treffen eine **steuerliche** Aussage über
 * Ertrag oder Aufwand, die dieses System nicht selbst treffen darf.
 */
export const guthabenKorrekturGrundSchema = z.enum(['anfangsbestand']);
/**
 * Richtung statt Vorzeichen (`D-6`) — `minderung` bucht `1796` im Soll,
 * `erhoehung` im Haben (`E-692`).
 *
 * Ein signierter Betrag lüde zu `-0.00` und zu Vorzeichenfehlern beim Lesen
 * ein; mit einer Richtungsspalte wird die Zusicherung zu einem Satz statt zu
 * einer Fallunterscheidung.
 */
export const guthabenKorrekturRichtungSchema = z.enum(['erhoehung', 'minderung']);
/**
 * Trifft die Korrektur einen Kunden oder den Topf als Ganzes (`D-9`)?
 *
 * `bestand` ist der Fall der Anfangsbestandskorrektur am Cutover-Tag: Das
 * Hauptbuch führt Guthaben **ohne** Kundenbezug (`journal_line.customer_id`
 * bleibt `NULL`, `E-691`), eine Hauptbuch-Seite je Kunde gibt es also nicht.
 */
export const guthabenKorrekturBezugSchema = z.enum(['kunde', 'bestand']);
/**
 * Welches Buch führt (`D-4`)?
 *
 * `nebenbuch_fuehrt`: Das Webinterface hatte den richtigen Stand, die Fibu
 * zieht nach — mit der Freigabe ist der Vorgang abgeschlossen.
 * `hauptbuch_fuehrt`: Die Fibu hat entschieden, das Webinterface muss
 * nachziehen — der Vorgang ist erst mit der Spiegelzeile abgeschlossen
 * (`E-700`), und bis dahin steht er in der Kontrollsicht
 * `guthaben_korrektur_spiegel_offen` (`E-701`).
 */
export const guthabenKorrekturSpiegelSchema = z.enum(['nebenbuch_fuehrt', 'hauptbuch_fuehrt']);
/** Die drei Zustände der anfügenden Kette (Migrationen `0126` und `0127`). */
export const guthabenKorrekturStatusSchema = z.enum(['angelegt', 'freigegeben', 'gespiegelt']);
/**
 * `POST /api/v1/guthaben/korrekturen` — die Anlage. Bucht **nicht**.
 *
 * **Die zwei Querbedingungen stehen hier UND in der Datenbank, und das ist
 * kein Versehen.** `guthaben_korrektur_bezug_kunde` (`0126`, `E-694`)
 * verlangt die Äquivalenz `(bezug = 'kunde') = (fs_kundennummer IS NOT
 * NULL)`, `guthaben_korrektur_bestand_ohne_kunde` verbietet einen
 * `customer_id` ohne Kundenbezug. Ohne die Wiederholung hier käme eine
 * Anlage mit `bezug: "kunde"` und ohne Nummer als roher `23514` aus dem
 * `INSERT` zurück — eine Meldung über einen Bedingungsnamen statt über die
 * Sache, und dem Aufrufer bliebe die Übersetzung überlassen. Die
 * **Durchsetzung** bleibt trotzdem unten: Der Vertrag deckt nur den Weg
 * durch HTTP, die Bedingung jeden.
 *
 * **`fsKundennummer` ist Text und ausdrücklich kein Fremdschlüssel** (`D-9`).
 * Ein Pflicht-Fremdschlüssel auf `customer` revidierte `F4` der Guthaben-Spec
 * — ein Debitor entsteht erst mit der ersten Rechnung — und bräche `E-431`.
 * `customerId` ist optional und wird **nie angelegt** (`E-702`): Wer keine
 * Rechnung hatte, hat keinen Debitor, und der Korrekturpfad erfindet keinen.
 *
 * **`buchungsdatum` gehört an die ANLAGE und nicht an die Freigabe**
 * (Cutover-Spec 7.3, `D-18` dort, Migration `0146`). Anlage und Freigabe sind
 * zwei Menschen (`guthaben_korrektur_vier_augen`, `E-685`); ein Datum, das
 * erst die Freigabe mitbrächte, müsste die zweite Person eintippen, und
 * nichts bände sie an das, was die erste gemeint hat. Ohne Angabe bucht die
 * Freigabe auf den Tag, an dem sie erteilt wird.
 */
export const guthabenKorrekturCreateRequestSchema = z
    .object({
    grund: guthabenKorrekturGrundSchema,
    richtung: guthabenKorrekturRichtungSchema,
    betrag: positiveAmount,
    bezug: guthabenKorrekturBezugSchema,
    fsKundennummer: z.string().trim().min(1, 'FS-Kundennummer darf nicht leer sein').optional(),
    customerId: uuid.optional(),
    spiegel: guthabenKorrekturSpiegelSchema,
    begruendung: z.string().trim().min(1, 'Begründung darf nicht leer sein').max(2000),
    buchungsdatum: isoDate.optional(),
})
    .strict()
    .superRefine((wert, ctx) => {
    if (wert.bezug === 'kunde' && wert.fsKundennummer === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fsKundennummer'],
            message: 'Eine Korrektur mit Bezug "kunde" trägt genau eine FS-Kundennummer.',
        });
    }
    if (wert.bezug === 'bestand' && wert.fsKundennummer !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fsKundennummer'],
            message: 'Eine Bestandskorrektur trifft den Topf als Ganzes und trägt keine FS-Kundennummer.',
        });
    }
    if (wert.bezug === 'bestand' && wert.customerId !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customerId'],
            message: 'Ein Debitor ohne Kundenbezug wäre eine Kundenspur an einer Zeile, die den Topf als Ganzes betrifft.',
        });
    }
});
/**
 * `POST /api/v1/guthaben/korrekturen/:id/freigabe` — der Rumpf.
 *
 * **`idempotencyKey` steht hier und nicht bei der Anlage**, dieselbe Wahl und
 * dieselbe Begründung wie bei `guthabenErstattungFreigabeRequestSchema`: Die
 * BUCHUNG entsteht erst mit diesem Aufruf, und genau seine Wiederholung nach
 * einem Netzwerkabbruch ist der Fall, den der Schlüssel abfängt.
 */
export const guthabenKorrekturFreigabeRequestSchema = z
    .object({
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
})
    .strict();
/**
 * `POST /api/v1/guthaben/korrekturen/:id/spiegelung` — der Rumpf.
 *
 * **`spiegelReferenz` ist Pflicht** (`0126`,
 * `guthaben_korrektur_spiegel_vollstaendig`, `E-699`): Was die Spiegelzeile
 * festhält, ist **dass ein Mensch das Webinterface nachgezogen hat** — mit
 * Namen, Zeit und Referenz. Ohne die Referenz bliebe von den dreien nur die
 * Behauptung.
 *
 * **`begruendung` ist freiwillig und überschreibt die der Vorgängerzeile.**
 * `0127` (`E-689`) lässt genau zwei Angaben zwischen den Zeilen der Kette
 * abweichen — `begruendung` und `customer_id` —, alle sieben anderen bleiben
 * unverändert. Der Spiegler darf deshalb seine eigene Begründung schreiben;
 * lässt er sie weg, wandert die des Freigebers mit.
 *
 * **Kein `idempotencyKey`.** Die Spiegelung bucht nicht (`E-699`), es gibt
 * also keine Buchung, deren Wiederholung ein Schlüssel abfangen könnte; eine
 * zweite Spiegelung derselben Freigabe ist
 * `GUTHABEN_KORREKTUR_ALREADY_MIRRORED`.
 */
export const guthabenKorrekturSpiegelungRequestSchema = z
    .object({
    spiegelReferenz: z.string().trim().min(1, 'Spiegelreferenz darf nicht leer sein').max(2000),
    begruendung: z.string().trim().min(1, 'Begründung darf nicht leer sein').max(2000).optional(),
})
    .strict();
/**
 * Eine Guthaben-Korrektur — die Antwort von Anlage, Freigabe und Spiegelung
 * und die Zeilenform der Liste.
 *
 * **`id` ist IMMER die Kennung der ANLAGE-Zeile, `zeileId` die der Zeile, aus
 * der dieser Stand stammt.** Das ist der Unterschied zur Erstattung, und er
 * ist keine Geschmacksfrage: Eine Korrektur hat bis zu DREI Zeilen
 * (`angelegt` → `freigegeben` → `gespiegelt`), und alle drei schreibenden
 * Endpunkte nehmen im Pfad die Kennung der **Anlage** (`ladeAnlage()` sucht
 * mit `ersetzt_korrektur_id IS NULL`). Trüge die Antwort der Freigabe die
 * Kennung der Freigabe**zeile**, führte der nächstliegende Weg — `id` aus der
 * Antwort nehmen und damit spiegeln — geradewegs in ein
 * `GUTHABEN_KORREKTUR_NOT_FOUND`. `id` ist deshalb der Griff am **Vorgang**,
 * `zeileId` der Nachweis, welche Zeile gerade das Ende der Kette ist.
 *
 * **`journalEntryId` ist erst ab `freigegeben` gesetzt** — die Anlage bucht
 * nicht (`E-688`) —, und die Spiegelzeile trägt es ebenfalls nicht (`E-699`):
 * Die Buchung hängt an der Freigabezeile und bleibt dort. Der aktuelle Stand
 * einer **gespiegelten** Korrektur zeigt deshalb `journalEntryId: null`,
 * obwohl gebucht wurde; die Buchung steht in der Kette (`GET .../:id`) auf
 * der Freigabezeile. Zwei Zeilen mit demselben `journalEntryId` liessen jede
 * Summenabfrage über diese Tabelle doppelt zählen — genau das verhindert
 * `guthaben_korrektur_spiegel_vollstaendig`.
 */
export const guthabenKorrekturSchema = z
    .object({
    id: uuid,
    zeileId: uuid,
    grund: guthabenKorrekturGrundSchema,
    richtung: guthabenKorrekturRichtungSchema,
    betrag: positiveAmount,
    bezug: guthabenKorrekturBezugSchema,
    fsKundennummer: z.string().nullable(),
    customerId: uuid.nullable(),
    spiegel: guthabenKorrekturSpiegelSchema,
    begruendung: z.string(),
    /** Vorgabe für `journal_entry.booking_date`; `null` = Tag der Freigabe. */
    buchungsdatum: isoDate.nullable(),
    status: guthabenKorrekturStatusSchema,
    journalEntryId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /** Einzelfreigabe (`D-31`, `0263`), siehe `guthabenErstattungSchema`. */
    einzelfreigabe: z.boolean().optional(),
    gespiegeltVon: uuid.nullable(),
    gespiegeltAm: z.string().nullable(),
    spiegelReferenz: z.string().nullable(),
    createdBy: uuid,
    createdAt: z.string(),
})
    .strict();
/**
 * `GET /api/v1/guthaben/korrekturen` — je Vorgang der **aktuelle** Stand,
 * gefiltert über Status, Grund und FS-Kundennummer.
 *
 * **Ein Eintrag je Vorgang und nicht je Zeile.** Die Kette einer Korrektur
 * gehört in die Einzelauskunft; eine Liste, in der derselbe Vorgang dreimal
 * steht, beantwortet keine Frage, die jemand hat.
 */
export const guthabenKorrekturListeSchema = z
    .object({
    korrekturen: z.array(guthabenKorrekturSchema),
})
    .strict();
/**
 * `GET /api/v1/guthaben/korrekturen/:id` — die ganze Kette, Anlage zuerst.
 *
 * Jeder Eintrag trägt dieselbe `id` (den Vorgang) und seine eigene `zeileId`.
 * Die Kette ist die einzige Ansicht, in der die Freigabezeile mit ihrem
 * `journalEntryId` sichtbar bleibt, nachdem die Spiegelung sie abgelöst hat.
 */
export const guthabenKorrekturKetteSchema = z
    .object({
    kette: z.array(guthabenKorrekturSchema),
})
    .strict();
/**
 * Eine Zeile der Kontrollsicht `guthaben_korrektur_spiegel_offen`
 * (Migration `0128`, `E-701`) — bewusst weniger Spalten als
 * `guthabenKorrekturSchema`.
 *
 * **Arbeitsliste, keine Muss-leer-sein-Kontrolle.** Jede Zeile ist eine
 * gebuchte Korrektur mit `spiegel = 'hauptbuch_fuehrt'`, deren Nachzug im
 * Webinterface noch aussteht — Arbeit, kein Verstoss. `pnpm
 * guthaben:spiegel-offen` endet aus demselben Grund mit `0`, auch wenn es
 * Zeilen findet.
 *
 * **`id` ist auch hier die Kennung der ANLAGE** und nicht die der
 * Freigabezeile, die die Sicht führt: Wer eine Zeile dieser Liste abarbeiten
 * will, ruft `POST .../:id/spiegelung` — und der Pfad nimmt die Anlage.
 */
export const guthabenKorrekturSpiegelOffenSchema = z
    .object({
    id: uuid,
    zeileId: uuid,
    grund: guthabenKorrekturGrundSchema,
    richtung: guthabenKorrekturRichtungSchema,
    betrag: positiveAmount,
    bezug: guthabenKorrekturBezugSchema,
    fsKundennummer: z.string().nullable(),
    begruendung: z.string(),
    journalEntryId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /** Einzelfreigabe (`D-31`, `0263`), siehe `guthabenErstattungSchema`. */
    einzelfreigabe: z.boolean().optional(),
    createdBy: uuid,
    createdAt: z.string(),
})
    .strict();
/** `GET /api/v1/guthaben/korrekturen/spiegel-offen` — der Arbeitsvorrat der Spiegelung. */
export const guthabenKorrekturSpiegelOffenListeSchema = z
    .object({
    korrekturen: z.array(guthabenKorrekturSpiegelOffenSchema),
})
    .strict();
/**
 * Der Filter der Liste (Spec 8.1): Status, Grund und FS-Kundennummer, alle
 * drei freiwillig. Bauform `aborechnungFilterSchema`.
 *
 * **Kein Filter über `bezug` oder `spiegel`.** Beide tragen genau zwei Werte
 * und teilen die Liste damit grob in zwei Hälften — ein Schnitt, der keinen
 * Blick spart; die Spec nennt ihn deshalb nicht.
 */
export const guthabenKorrekturFilterSchema = z
    .object({
    status: guthabenKorrekturStatusSchema.optional(),
    grund: guthabenKorrekturGrundSchema.optional(),
    fsKundennummer: z.string().trim().min(1, 'FS-Kundennummer darf nicht leer sein').optional(),
})
    .strict();
//# sourceMappingURL=guthaben.js.map